// Die Agenten aus dem Homelab-Protokoll (Topics `agent.stand`, `agent.kandidat`;
// docs/protokoll.md › „Agenten über das Protokoll“) in genau die Form, die die
// Seiten aus den REST-Antworten GET /v2-api/agents und /agents/candidates
// kennen (`AgentView`, `AgentCandidate` aus agents-summary.svelte.ts).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/agenten-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("agent.stand", …)`: Takt-Messung in Sekunden bzw. Prozent);
// Gegenstück der Rechnung ist internal/protokoll/adapter/agenten.go.
//
// Regel, mit der die REST-Antwort exakt wiederkehrt (der Adapter sendet
// spiegelbildlich, wie bei Licht und Speicher):
//   - Felder, die die REST-Antwort ohne `omitempty` führt (Name, Art, Topic,
//     Intervalle, aktiv, Notiz, Zeitstempel, Status): fehlt der Wert im Stand,
//     wird er ""/0/false. (Der Adapter lässt leere Texte weg, Zahlen und
//     Wahrheitswerte ohne omitempty sendet er immer.)
//   - Felder mit `omitempty` stehen am Ergebnis nur, wenn der Stand sie trägt.
//   - Zeiten kommen als Unix-Millisekunden und werden zu ISO-Text; `deploy`
//     steht nur mit Objekt, seine Zeiten nur, wenn sie je gesetzt waren.
//   - Die Id des Agenten steht nur im Instanznamen („agent.12.stand“).
//   - `last_seen_age_s` steht nicht im Stand: es rechnet sich aus `last_seen`
//     und der Uhr des Browsers (`alterS`); der Store tickt es nach.

import type { AgentCandidate, AgentStatus, AgentView, DeployState, DeployStatus } from "./agents-summary.svelte";
import type { AgentDeploy, AgentKandidat, AgentStand } from "./protokoll/schema.gen";

/** Instanzname → Id des Agenten: „agent.12.stand“ → 12. Alles andere → undefined. */
export function agentIdAusInstanz(instanz: string): number | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "agent" || t[2] !== "stand" || !/^[0-9]+$/.test(t[1])) return undefined;
  const id = Number(t[1]);
  return Number.isSafeInteger(id) ? id : undefined;
}

/** Instanzname → Kennung des Kandidaten: „agent.kandidat.ab12…“ → „ab12…“. Alles andere → undefined. */
export function kandidatKennungAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "agent" || t[1] !== "kandidat" || t[2] === "") return undefined;
  return t[2];
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const n = (v: number | null | undefined): number => v ?? 0;
const b = (v: boolean | null | undefined): boolean => v ?? false;

/** Unix-Millisekunden → ISO-Text; fehlt/unlesbar → undefined. */
function iso(ms: number | null | undefined): string | undefined {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return undefined;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Setzt `o[k]`, wenn der Stand einen Wert trägt (auch 0/false/""). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v != null) o[k] = v as T[K];
}

function deploy(d: AgentDeploy): DeployState {
  const o: DeployState = { status: s(d.status) as DeployStatus, attempts: n(d.versuche) };
  const versuch = iso(d.versuch);
  if (versuch !== undefined) o.last_try = versuch;
  const erfolg = iso(d.erfolg);
  if (erfolg !== undefined) o.last_ok = erfolg;
  if (d.fehler) o.last_err = d.fehler;
  const unerreichbar = iso(d.unerreichbar_seit);
  if (unerreichbar !== undefined) o.unreachable_since = unerreichbar;
  return o;
}

/** Der Agent aus seinem Stand, wie `GET /v2-api/agents` ihn führt -- ohne
 *  `last_seen_age_s` (siehe `setzeAlter`). `id` kommt aus dem Instanznamen. */
export function agentAusProtokoll(id: number, a: AgentStand): AgentView {
  const o: AgentView = {
    id,
    name: s(a.name),
    kind: s(a.art),
    mqtt_topic: s(a.mqtt_topic),
    expected_interval_s: n(a.lebenszeichen_s),
    data_interval_s: n(a.daten_s),
    max_silence_s: n(a.max_stille_s),
    ssh_alias: s(a.ssh_alias),
    enabled: b(a.aktiv),
    notes: s(a.notiz),
    status: s(a.status) as AgentStatus,
    created_at: s(a.angelegt),
    updated_at: s(a.geaendert),
  };
  const zuletzt = iso(a.zuletzt);
  if (zuletzt !== undefined) o.last_seen = zuletzt;
  const abgemeldet = iso(a.abgemeldet_seit);
  if (abgemeldet !== undefined) o.offline_since = abgemeldet;
  if (a.deploy != null) o.deploy = deploy(a.deploy);
  wenn(o, "measured_interval_s", a.gemessen_s);
  wenn(o, "interval_samples", a.proben);
  wenn(o, "interval_skew_pct", a.abweichung);
  wenn(o, "expected_interval_effective_s", a.soll_s);
  return o;
}

/** Der Kandidat aus seinem Stand, wie `GET /v2-api/agents/candidates` ihn führt. */
export function kandidatAusProtokoll(k: AgentKandidat): AgentCandidate {
  return {
    topic: s(k.topic),
    kind: s(k.art),
    default_name: s(k.name),
    frame_count: n(k.frames),
    first_seen: s(k.erstmals),
    last_seen: s(k.zuletzt),
  };
}

/** Alter in Sekunden seit `lastSeen` (ISO-Text) zur Zeit `jetztMs`; nie
 *  negativ (eine Uhr, die hinterherhinkt, zeigt „vor 0 s“). Ohne lesbares
 *  `lastSeen` → undefined. */
export function alterS(lastSeen: string | undefined, jetztMs: number): number | undefined {
  if (!lastSeen) return undefined;
  const t = Date.parse(lastSeen);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, (jetztMs - t) / 1000);
}

/** Trägt `last_seen_age_s` in den Agenten ein (oder nimmt es heraus, wenn er
 *  noch nie gesehen wurde), nur bei Änderung -- am reaktiven Objekt meldet
 *  jedes Schreiben sonst eine Änderung. */
export function setzeAlter(a: AgentView, jetztMs: number): void {
  const alter = alterS(a.last_seen, jetztMs);
  if (alter === undefined) {
    if ("last_seen_age_s" in a) delete a.last_seen_age_s;
  } else if (a.last_seen_age_s !== alter) {
    a.last_seen_age_s = alter;
  }
}

/** Die Reihenfolge der REST-Liste der Agenten (ORDER BY name, id; SQLite
 *  vergleicht den Namen Byte für Byte, hier Codeeinheit für Codeeinheit --
 *  gleich, solange kein Name Zeichen außerhalb der Grundebene trägt). */
export function vergleicheAgenten(a: Pick<AgentView, "name" | "id">, b2: Pick<AgentView, "name" | "id">): number {
  if (a.name !== b2.name) return a.name < b2.name ? -1 : 1;
  return a.id - b2.id;
}

/** Die Reihenfolge der REST-Liste der Kandidaten (ORDER BY last_seen DESC; bei
 *  Gleichstand ist die Reihenfolge der Datenbank offen -- hier nach Topic). */
export function vergleicheKandidaten(a: Pick<AgentCandidate, "last_seen" | "topic">, b2: Pick<AgentCandidate, "last_seen" | "topic">): number {
  if (a.last_seen !== b2.last_seen) return a.last_seen < b2.last_seen ? 1 : -1;
  if (a.topic !== b2.topic) return a.topic < b2.topic ? -1 : 1;
  return 0;
}
